import {
  BookOpen,
  Database,
  Ellipsis,
  Home,
  LogOut,
  MessageSquarePlus,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Search,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { APP_ROUTES } from "../routes";

const primaryItems = [
  { icon: Home, label: "Home", href: APP_ROUTES.datascope },
  { icon: Pin, label: "Pinboards", href: APP_ROUTES.pinboards },
  { icon: Sparkles, label: "Agents", href: APP_ROUTES.agents },
  { icon: BookOpen, label: "Knowledge", href: APP_ROUTES.knowledgebase },
];

const moreItems = [
  { icon: Search, label: "Gaps", href: APP_ROUTES.knowledgeGaps },
  { icon: Database, label: "Explore", href: APP_ROUTES.explore },
];

const makeUserName = (email: string | undefined): string => {
  if (!email) {
    return "User";
  }

  const [name] = email.split("@");
  return name.replace(/[._-]+/g, " ");
};

export function UserMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const userDisplayName = makeUserName(user?.email);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isActive = (href: string) => location.pathname.startsWith(href);
  const isMoreActive = moreItems.some((item) => isActive(item.href));

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (accountMenuRef.current?.contains(target) || moreMenuRef.current?.contains(target)) {
        return;
      }

      setAccountOpen(false);
      setMoreOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  return (
    <aside
      className={`flex h-dvh select-none flex-col overflow-hidden bg-[#f7f7f7] transition-all duration-200 ${
        collapsed ? "w-[64px]" : "w-[260px]"
      }`}
    >
      <div className={collapsed ? "p-3" : "px-4 pb-2 pt-4"}>
        {!collapsed ? (
          <div className="flex items-center justify-between gap-3">
            <Link
              to={APP_ROUTES.datascope}
              className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-[16px] font-semibold leading-none text-[#111111] transition-colors hover:bg-[#ececec]"
            >
              <img src="/logo_small.svg" alt="" className="h-5 w-5 shrink-0" />
              <span className="truncate">Datascope</span>
            </Link>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6b6b6b] transition-colors hover:bg-[#ececec] hover:text-[#111111]"
              title="Close sidebar"
            >
              <PanelLeftClose className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6b6b6b] transition-colors hover:bg-[#ececec] hover:text-[#111111]"
              title="Open sidebar"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </button>
            <Link
              to={APP_ROUTES.datascope}
              className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-[#ececec]"
              title="Datascope"
            >
              <img src="/logo_small.svg" alt="" className="h-5 w-5" />
            </Link>
          </div>
        )}
      </div>

      <nav className="thin-scrollbar min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-2">
        {!collapsed ? (
          <div className="space-y-1">
            <Link
              to={APP_ROUTES.datascope}
              className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#ececec]"
            >
              <MessageSquarePlus className="h-[18px] w-[18px] text-[#202123]" />
              <span>New chat</span>
            </Link>

            {primaryItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium transition-colors ${
                    active
                      ? "bg-[#ececec] text-[#111111]"
                      : "text-[#2f2f2f] hover:bg-[#ececec]"
                  }`}
                >
                  <Icon className="h-[18px] w-[18px] text-[#202123]" />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            <div ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setAccountOpen(false);
                  setMoreOpen((current) => !current);
                }}
                className={`flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium transition-colors ${
                  isMoreActive
                    ? "bg-[#ececec] text-[#111111]"
                    : "text-[#2f2f2f] hover:bg-[#ececec]"
                }`}
              >
                <Ellipsis className="h-[18px] w-[18px] text-[#202123]" />
                <span>More</span>
              </button>
              {moreOpen ? (
                <div className="rounded-2xl border border-[#e4e4e4] bg-white p-1.5 shadow-[0_14px_34px_rgba(0,0,0,0.1)]">
                  {moreItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);

                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMoreOpen(false)}
                        className={`flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium transition-colors ${
                          active
                            ? "bg-[#ececec] text-[#111111]"
                            : "text-[#2f2f2f] hover:bg-[#f3f3f3]"
                        }`}
                      >
                        <Icon className="h-[18px] w-[18px] text-[#202123]" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {[MessageSquarePlus, ...primaryItems.map((item) => item.icon), Ellipsis].map(
              (Icon, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() =>
                    index === 0
                      ? navigate(APP_ROUTES.datascope)
                      : index === primaryItems.length + 1
                        ? setMoreOpen((current) => !current)
                        : navigate(primaryItems[index - 1]!.href)
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-[#2f2f2f] transition-colors hover:bg-[#ececec]"
                >
                  <Icon className="h-5 w-5" />
                </button>
              ),
            )}
          </div>
        )}
      </nav>

      {!collapsed ? (
        <div ref={accountMenuRef} className="relative px-3 pb-3">
          {accountOpen ? (
            <div className="absolute bottom-[68px] left-3 right-3 z-30 rounded-2xl border border-[#e4e4e4] bg-white p-1.5 shadow-[0_18px_42px_rgba(0,0,0,0.14)]">
              <Link
                to={APP_ROUTES.settings}
                onClick={() => setAccountOpen(false)}
                className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#f3f3f3]"
              >
                <Settings className="h-[18px] w-[18px]" />
                <span>Settings</span>
              </Link>
              <Link
                to={APP_ROUTES.users}
                onClick={() => setAccountOpen(false)}
                className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#f3f3f3]"
              >
                <Users className="h-[18px] w-[18px]" />
                <span>Users</span>
              </Link>
              <button
                type="button"
                onClick={() => void handleLogout()}
                className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium text-[#2f2f2f] transition-colors hover:bg-[#f3f3f3]"
              >
                <LogOut className="h-[18px] w-[18px]" />
                <span>Log out</span>
              </button>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setMoreOpen(false);
              setAccountOpen((current) => !current);
            }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[#2f2f2f] transition-colors hover:bg-[#ececec]"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#d9d9d9] text-[12px] font-semibold text-[#555555]">
              {userDisplayName.slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-medium leading-5">
                {userDisplayName}
              </div>
              <div className="truncate text-[12px] leading-4 text-[#777777]">
                {user?.email ?? "Personal account"}
              </div>
            </div>
          </button>
        </div>
      ) : null}
    </aside>
  );
}
